import {
  useSuspenseQuery,
  type UseSuspenseQueryResult,
  type UseSuspenseQueryOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { getWfAuthnClient, isWfAuthnMockEnabled, redirectToLogin, setAuthCookies, useAxios } from "@wayflyer/auth-sdk";
import { BaseSchemas } from "@wayflyer/api-types";
import { isAxiosError } from "axios";
import type { AxiosError, AxiosResponse } from "axios";
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { OpenApi } from "@wayflyer/api-types/staff";
import { CUSTOMER_UUID_QUERY_KEY } from "shared-utils";

type StaffAppConfigSchemaOut = OpenApi.StaffAppConfigSchemaOut;

export const STAFF_APP_CONFIG_QUERY_KEY = "staff-app-config";
export const CUSTOMER_APP_CONFIG_QUERY_KEY = "customer-app-config";

// The token can be valid while the backend still can't resolve a user from it: the email is claimed by another login
// method (400), or the account has been deactivated (403). The `/authn-wf/*` page explaining that still needs *an* app
// config so it can render rather than tripping the error boundary onto a generic error page. There is no authenticated
// user in this state, so this mirrors the unauthenticated/public config the backend would otherwise send.
const UNRESOLVED_USER_FALLBACK_APP_CONFIG: BaseSchemas.CustomerAppConfig = {
  analyticsAttributes: {
    companyPublicId: null,
    createdAt: null,
    dealType: null,
    hubspotContactId: null,
    lastLogin: null,
    productRequestPublicId: null,
    userId: null,
    userUuid: null,
  },
  customerDomain: "",
  customerName: "",
  featureFlags: {},
  googleClientId: "",
  hasInviteCookie: false,
  homePath: "",
  logoPath: "",
  logoutPath: "",
  navItems: [],
  userEmail: "",
  userName: "",
  userPermissions: [],
  userRole: null,
};

export const useStaffAppConfig = (): UseSuspenseQueryResult<StaffAppConfigSchemaOut> => {
  const headers = { "x-current-location": window.location.pathname };
  const axiosClient = useAxios();
  return useSuspenseQuery<AxiosResponse<StaffAppConfigSchemaOut>, AxiosError, StaffAppConfigSchemaOut>({
    queryKey: [STAFF_APP_CONFIG_QUERY_KEY],
    queryFn: async () => {
      if (!isWfAuthnMockEnabled()) {
        // HACK: Blast in access token cookies incase they became expired.
        // Since this gets called almost immediately and suspends everything else,
        // it's a decent place to inject them.
        const client = getWfAuthnClient();
        let accessToken = await client.getAccessToken();
        if (accessToken === undefined) {
          accessToken = await client.getAccessToken({ refresh: true });
          if (accessToken === undefined) {
            const next = window.location.pathname + window.location.search;
            window.location.href = "/authn/login-staff?next=" + encodeURIComponent(next);
            throw new Error("Can't get access token, redirecting to login");
          }
        }
        setAuthCookies({ accessToken: accessToken.jwt });
      }
      return axiosClient.get<StaffAppConfigSchemaOut>("/api/staff/v2/api/app-config", { headers });
    },
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
    select: ({ data }) => data,
  });
};
// Sends the user to login, returning them to the *page* they were on (not the API URL that failed) once they
// re-authenticate. Returns a never-settling promise so a suspense query keeps showing its fallback while the browser
// navigates away, rather than briefly rendering the error boundary.
const redirectToLoginAndSuspend = (): Promise<never> => {
  redirectToLogin();
  return new Promise<never>(() => {});
};

type AppConfigErrorData = {
  typ?: unknown;
  should_login_as?: unknown;
};

export type CustomerAppConfigAuthError =
  | { typ: "email_login_constraint"; shouldLoginAs: "credentials" | "google" }
  | { typ: "user_deactivated" };

export type CustomerAppConfig = BaseSchemas.CustomerAppConfig & {
  authError?: CustomerAppConfigAuthError;
};

export const getUnresolvedUserAuthError = (
  status: number | undefined,
  data: unknown
): CustomerAppConfigAuthError | null => {
  if (typeof data !== "object" || data === null) {
    return null;
  }

  const { typ, should_login_as: shouldLoginAs } = data as AppConfigErrorData;
  if (status === 403 && typ === "user_deactivated") {
    return { typ };
  } else if (
    status === 400 &&
    typ === "email_login_constraint" &&
    (shouldLoginAs === "credentials" || shouldLoginAs === "google")
  ) {
    return { typ, shouldLoginAs };
  }
  return null;
};

const getUnresolvedUserFallbackResponse = (error: AxiosError): AxiosResponse<CustomerAppConfig> | null => {
  const authError = getUnresolvedUserAuthError(error.status, error.response?.data);
  if (!authError) {
    return null;
  }
  return {
    ...error.response,
    data: { ...UNRESOLVED_USER_FALLBACK_APP_CONFIG, authError },
  } as AxiosResponse<CustomerAppConfig>;
};

export const useCustomerAppConfig = (
  options: Omit<
    UseSuspenseQueryOptions<AxiosResponse<CustomerAppConfig>, AxiosError, CustomerAppConfig>,
    "queryKey" | "queryFn" | "select"
  > = {}
): UseSuspenseQueryResult<CustomerAppConfig> => {
  const { customerDomain } = useParams();
  const queryClient = useQueryClient();
  const axiosClient = useAxios();
  const isAuthPath = location.pathname.includes("authn-wf");
  const path = customerDomain ? `/api/${customerDomain}/app-config` : "/api/app-config";
  return useSuspenseQuery<AxiosResponse<CustomerAppConfig>, AxiosError, CustomerAppConfig>({
    queryKey: [CUSTOMER_APP_CONFIG_QUERY_KEY, customerDomain ? customerDomain : "public"],
    queryFn: async () => {
      try {
        const response = await axiosClient.get<CustomerAppConfig>(path);

        // Cache the customerUuid for the duration of the session
        if (response.data.customerUuid) {
          queryClient.setQueryDefaults([CUSTOMER_UUID_QUERY_KEY], {
            staleTime: Infinity,
            gcTime: Infinity,
          });
          queryClient.setQueryData([CUSTOMER_UUID_QUERY_KEY], response.data.customerUuid);
        }

        return response;
      } catch (e: unknown) {
        if (isAxiosError(e)) {
          // Unauthenticated: the backend returns a 401 here rather than an HTML redirect to login (which we couldn't
          // follow, and whose `next` would point at this API URL). Send the user to login, returning them to the
          // *page* they were on once they re-authenticate.
          if (e.status === 401) {
            return redirectToLoginAndSuspend();
          }
          // If we get a 404, the customer domain probably doesn't exist, so fall back to the public app config. That
          // endpoint is unauthenticated and normally succeeds, but if it ever responds 401 we treat it like an
          // unauthenticated primary request and send the user to login rather than throwing to the error boundary.
          if (e.status === 404) {
            try {
              return await axiosClient.get<CustomerAppConfig>("/api/app-config");
            } catch (fallbackError: unknown) {
              if (isAxiosError(fallbackError) && fallbackError.status === 401) {
                return redirectToLoginAndSuspend();
              }
              if (isAxiosError(fallbackError)) {
                const fallbackResponse = getUnresolvedUserFallbackResponse(fallbackError);
                if (fallbackResponse) {
                  return fallbackResponse;
                }
              }
              throw fallbackError;
            }
          }
          const fallbackResponse = getUnresolvedUserFallbackResponse(e);
          if (fallbackResponse) {
            return fallbackResponse;
          }
        }
        throw e;
      }
    },
    select: ({ data }) => data,
    refetchOnMount: !isAuthPath,
    staleTime: isAuthPath ? 60 * 1000 : 0,
    ...options,
  });
};
